export interface Plan {
  id: string;
  name: string;
  label: string;
  description?: string;
  maxMembers: number | null; // null = unlimited
  maxCpuQuota: number | null; // null = unlimited
  maxStorageGb: number | null; // null = unlimited
  pricePerMonthUsd: number;
  /** Stripe Price lookup keys. A plan is sold via Stripe when it has either: a base price is a
   *  flat monthly fee; a metered price bills usage. PAY AS YOU GO is metered-only ($0 base). */
  stripeBasePriceLookupKey?: string | null;
  stripeMeteredPriceLookupKey?: string | null;
  /** Prepaid compute credit granted each paid month, in dollars — a prepaid tier's monthly
   *  fee comes back in full as this credit. */
  includedComputeUnitsPerMonth?: number | null;
  /** False = sold by contract ("Contact us"), never through Checkout. */
  isSelfServe?: boolean;
  /** The trust ramp: purchasable only once the organization has a paid invoice. */
  requiresPaymentHistory?: boolean;
  /** Unbilled usage (USD) at which usage is charged mid-month instead of at month end. */
  spendThresholdUsd?: number | null;
  includesComputeCost: boolean;
  includesStorageCost: boolean;
  isActive: boolean;
  sortOrder: number;
  /** Sev1 (24×7 paged) support entitlement — enterprise only by default. */
  allowsSev1?: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Plan enforcement limits the API surfaces per-organization. Not yet part of
 * the Plan row itself as of 2026-04 — tracked here for future alignment.
 */
export interface PlanUsage {
  organizationId: string;
  memberCount: number;
  projectCount: number;
  environmentCount: number;
  databaseClusterCount: number;
}

// Plan names as constants for type-safe comparisons
export const PLAN_NAMES = {
  FREE: 'free',
  STANDARD: 'standard',
  ENTERPRISE: 'enterprise',
} as const;

export type PlanName = typeof PLAN_NAMES[keyof typeof PLAN_NAMES];

// Helper functions for plan limits
export function isUnlimited(value: number | null): boolean {
  return value === null;
}

export function formatLimit(value: number | null, unit?: string): string {
  if (value === null) {
    return 'Unlimited';
  }
  return unit ? `${value} ${unit}` : value.toString();
}

// Accepts number | string because the API serializes the Decimal
// `price_per_month_usd` as a JSON string (e.g. "0.00"); planService coerces it,
// but stay defensive for any other call site / mock shape.
export function formatPrice(priceUsd: number | string): string {
  const n = typeof priceUsd === 'number' ? priceUsd : Number(priceUsd);
  if (!n || Number.isNaN(n)) {
    return 'Free';
  }
  return `$${n.toFixed(2)}/month`;
}

/** Sold via Stripe (needs a subscription = a card on file)? Mirrors the API's Plan.is_stripe_billable. */
export const isStripeBillable = (plan: Pick<Plan, 'stripeBasePriceLookupKey' | 'stripeMeteredPriceLookupKey'>): boolean =>
  Boolean(plan.stripeBasePriceLookupKey || plan.stripeMeteredPriceLookupKey);

/** Usage-only plan: no flat fee, billed for the hours clusters run. */
export const isPayAsYouGo = (plan: Pick<Plan, 'stripeBasePriceLookupKey' | 'stripeMeteredPriceLookupKey'>): boolean =>
  !plan.stripeBasePriceLookupKey && Boolean(plan.stripeMeteredPriceLookupKey);

/** A plan's headline price. Pay-as-you-go has a $0 base but is NOT free — it bills usage. */
/** A prepaid tier: a monthly fee charged up front that comes back as compute credit. */
export const isPrepaidTier = (plan: Pick<Plan, 'includedComputeUnitsPerMonth'>): boolean =>
  Number(plan.includedComputeUnitsPerMonth ?? 0) > 0;

/** "8 vCPUs" / "Unlimited vCPUs" — a plan's cap on vCPUs running at once. */
export const formatCpuQuota = (quota: number | null | undefined): string =>
  quota == null ? 'Unlimited vCPUs' : `${quota.toLocaleString()} vCPU${quota === 1 ? '' : 's'}`;

export const formatPlanPrice = (
  plan: Pick<Plan, 'pricePerMonthUsd' | 'stripeBasePriceLookupKey' | 'stripeMeteredPriceLookupKey' | 'isSelfServe'>,
): string => (
  plan.isSelfServe === false ? 'Custom pricing'   // sold by contract — a list price would mislead
    : isPayAsYouGo(plan) ? 'Pay per hour' : formatPrice(plan.pricePerMonthUsd));
